'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  PageHeader,
  Pagination,
  SearchInput,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@rr/ui'
import { useVehiclesList } from '@/modules/vehicles/hooks/useVehicles'

const PAGE_SIZE = 10

export default function VehiclesPage() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
    }),
    [page, search]
  )

  const { data, isLoading, isError, error } = useVehiclesList(filters as any)
  const items: any[] = (data as any)?.items ?? (data as any)?.data?.items ?? []
  const total: number =
    (data as any)?.pagination?.total ??
    (data as any)?.data?.pagination?.total ??
    items.length

  return (
    <div className="page-pad">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          title="Vehicles"
          subtitle="Vehicle inventory across every tenant fleet."
        />

        <EntityListShell
          toolbar={
            <SearchInput
              value={search}
              onChange={(v) => {
                setSearch(v)
                setPage(1)
              }}
              placeholder="Search vehicles…"
              className="max-w-sm"
            />
          }
          empty={!isLoading && items.length === 0}
          emptyState={
            isError
              ? error instanceof Error
                ? error.message
                : 'Failed to load vehicles.'
              : 'No vehicles match your filters.'
          }
          pagination={
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={setPage}
            />
          }
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle #</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row: any) => {
                  const id = String(row.id ?? '')
                  const number =
                    row.vehicleNumber ??
                    row.plate ??
                    row.plateNumber ??
                    row.number ??
                    '—'
                  const type =
                    row.transportType?.name ??
                    row.transportTypeName ??
                    row.type ??
                    '—'
                  const carrier =
                    row.carrier?.name ??
                    row.carrierName ??
                    row.carrier ??
                    '—'
                  const status = String(row.status ?? 'active')
                  return (
                    <TableRow key={id || number}>
                      <TableCell className="font-medium text-foreground font-mono">
                        {number}
                      </TableCell>
                      <TableCell className="text-sm">{type}</TableCell>
                      <TableCell className="text-sm">{carrier}</TableCell>
                      <TableCell>
                        <StatusChip status={status} />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </EntityListShell>
      </div>
    </div>
  )
}
